import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { SideBarState } from "../../common/types/SliceTypes";
import { RootState } from "../store";
import { AXIOS } from "../../api/axios";

const initial: SideBarState = {
  reminders: [],
  receivedDocIds: [],
  sharedDocIds: [],
  sharedRecipients: [],
  activeLOIFFShareLevel: "",
  filteredDocIds: [],
  userSharedLOIList: [],
  isSharingEnabled: true,
  sharedDocIdToUser: {},
  receivedDocIdToUser: {},
};

export const sidebarSlice = createSlice({
  name: "sideBar",
  initialState: initial,
  reducers: {
    setReminders: (state, action) => {
      state.reminders = action.payload;
    },
    setReceivedDocIds: (state, action) => {
      state.receivedDocIds = action.payload;
    },
    setSharedDocIds: (state, action) => {
      state.sharedDocIds = action.payload;
    },
    setSharedRecipients: (state, action) => {
      state.sharedRecipients = action.payload;
    },
    setActiveLOIFFShareLevel: (state, action) => {
      state.activeLOIFFShareLevel = action.payload;
    },
    setFilteredDocIds: (state, action) => {
      state.filteredDocIds = action.payload;
    },
    setUserSharedLOIList: (state, action) => {
      state.userSharedLOIList = action.payload;
    },
    setIsSharingEnabled: (state, action) => {
      state.isSharingEnabled = action.payload;
    },
    setSharedDocToUserRecord: (state, action) => {
      state.sharedDocIdToUser = action.payload;
    },
    setReceviedDocToUserRecord: (state, action) => {
      state.receivedDocIdToUser = action.payload;
    },
    removeDocumentFromSharedList: (state, action) => {
      const documentIdToRemove = action.payload.documentId;
      state.userSharedLOIList = state.userSharedLOIList.map(
        (loiShareLevel) => ({
          ...loiShareLevel,
          docIds: loiShareLevel.docIds.filter(
            (id) => id !== documentIdToRemove
          ),
        })
      );
      state.sharedDocIds = state.sharedDocIds.filter(
        (id) => id !== documentIdToRemove
      );
    },
    updateUsernameForHash: (
      state,
      action: PayloadAction<{
        hash: string;
        currentEmail: string;
        newEmail: string;
      }>
    ) => {
      const { hash, currentEmail, newEmail } = action.payload;

      state.userSharedLOIList = state.userSharedLOIList.map((item) => {
        if (item.hash !== hash) return item;

        return {
          ...item,
          users: item.users.map((user) =>
            user.userEmailId === currentEmail
              ? { ...user, username: newEmail }
              : user
          ),
        };
      });
    },
  },
});

export const {
  setReminders,
  setReceivedDocIds,
  setSharedDocIds,
  setSharedRecipients,
  setActiveLOIFFShareLevel,
  setFilteredDocIds,
  setUserSharedLOIList,
  setIsSharingEnabled,
  setReceviedDocToUserRecord,
  setSharedDocToUserRecord,
  removeDocumentFromSharedList,
  updateUsernameForHash,
} = sidebarSlice.actions;

export default sidebarSlice.reducer;

export const selectReminders = (state: RootState) => state.sidebar.reminders;

export const selectSharedDocToUserRecord = (state: RootState) =>
  state.sidebar.sharedDocIdToUser;

export const selectReceivedDocToUserRecord = (state: RootState) =>
  state.sidebar.receivedDocIdToUser;

export const selectRecievedDocIds = (state: RootState) =>
  state.sidebar.receivedDocIds;

export const selectShareDocIds = (state: RootState) =>
  state.sidebar.sharedDocIds;

export const selectSharedRecipients = (state: RootState) =>
  state.sidebar.sharedRecipients;

export const selectActiveLOIFFShareLevel = (state: RootState) =>
  state.sidebar.activeLOIFFShareLevel;

export const selectFilteredDocIds = (state: RootState) =>
  state.sidebar.filteredDocIds;

export const selectUserSharedLOIList = (state: RootState) =>
  state.sidebar.userSharedLOIList;

export const selectAllSharedDocIds = (state: RootState) =>
  state.sidebar.userSharedLOIList.flatMap((item) => item.docIds);

export const selectSharedUsersForActiveLevel = (state: RootState) => {
  const active = state.sidebar.activeLOIFFShareLevel;

  const matched = state.sidebar.userSharedLOIList.find(
    (item) =>
      item.value === active &&
      (active === "inbox"
        ? item.docIds.includes(state.dashboard.selectedDocument.id)
        : true)
  );

  return matched?.users || [];
};

export const selectIsSharingEnabled = (state: RootState) =>
  state.sidebar.isSharingEnabled;

export const fetchReminders = createAsyncThunk(
  "reminders/",
  async (_, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL + "reminder/" + state.user.externalId;
    try {
      const response = await AXIOS.get(url);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

// export const fetchSharedDocsForUser = createAsyncThunk('shared/to', async (_, { getState }) => {
//   const state = getState() as RootState;
//   let url = process.env.REACT_APP_API_BASE_URL + 'filter/shared/to/' + state.user.externalId;
//   try {
//     const response = await AXIOS.get(url);
//     return response.data.data;
//   } catch (err) {
//     console.error(err);
//   }
// });

// export const fetchSharedDocsByUser = createAsyncThunk('shared/by', async (_, { getState }) => {
//   const state = getState() as RootState;
//   let url = process.env.REACT_APP_API_BASE_URL + 'filter/shared/by/' + state.user.externalId;
//   try {
//     const response = await AXIOS.get(url);
//     return response.data.data;
//   } catch (err) {
//     console.error(err);
//   }
// });

export const fetchSharedDocsForUser = createAsyncThunk(
  "shared/to",
  async (_, { getState }) => {
    const state = getState() as RootState;
    let url = process.env.REACT_APP_API_BASE_URL + "/share/doc/to";
    try {
      const data = state.user.email;
      const response = await AXIOS.post(url, data, {
        headers: { "Content-Type": "text/plain" },
      });
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const fetchSharedDocsByUser = createAsyncThunk(
  "shared/by",
  async (_, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "/share/doc/by/" +
      state.user.externalId;
    try {
      const response = await AXIOS.get(url);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const shareLOIFFilter = createAsyncThunk(
  "share/loiFFilter",
  async (
    payload: {
      docId: string[];
      userEmailId: string;
      value: string;
      nickname: string;
      notify: boolean;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "share/" +
      state.user.externalId +
      "?notify=" +
      payload.notify;
    try {
      const body = {
        docIds: payload.docId,
        emailId: payload.userEmailId,
        value: payload.value,
        nickName: payload.nickname,
      };
      const response = await AXIOS.post(url, body);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const fetchFFSharedDetailsByUser = createAsyncThunk(
  "shared/details/by",
  async (_, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL + "share/" + state.user.externalId;
    try {
      const response = await AXIOS.get(url);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const removeUserFromSharedLOIFFilter = createAsyncThunk(
  "remove/shared/loiFFilter",
  async (payload: { hash: string; userEmailId: string }, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "/share/remove/usrShared/" +
      state.user.externalId;
    try {
      const body = {
        hash: payload.hash,
        userEmail: payload.userEmailId,
      };
      const response = await AXIOS.put(url, body);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const toggleLOIShareState = createAsyncThunk(
  "toggle/sharing",
  async (payload: { enabled: boolean; hash: string }, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "/share/" +
      (payload.enabled ? "enable" : "disable") +
      "/" +
      state.user.externalId;
    try {
      const body = {
        hash: payload.hash,
      };
      const response = await AXIOS.put(url, body);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const removeLOIFromSharedAccess = createAsyncThunk(
  "remove/loi/from/shared/access",
  async (payload: { hash: string; docId: string }, { getState }) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "/share/remove/doc/" +
      state.user.externalId;
    try {
      const body = {
        hash: payload.hash,
        docIds: [payload.docId],
      };
      const response = await AXIOS.put(url, body);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);

export const updateLOIFFilterUserShareInfo = createAsyncThunk(
  "update/loiFFilter/user/share/info",
  async (
    payload: {
      hash: string;
      newNickname: string;
      emaildId: string;
      notify: boolean;
    },
    { getState }
  ) => {
    const state = getState() as RootState;
    let url =
      process.env.REACT_APP_API_BASE_URL +
      "/share/usr/name/" +
      state.user.externalId +
      "?notify=" +
      payload.notify;
    try {
      const body = {
        hash: payload.hash,
        currentEmail: payload.emaildId,
        newEmail: payload.newNickname,
      };
      const response = await AXIOS.put(url, body);
      return response.data.data;
    } catch (err) {
      console.error(err);
    }
  }
);
